/**
 * The "N permissions" pill on a role row, upgraded from a static count to a
 * click-to-reveal popover listing the actual grants — grouped by subject, each
 * action carrying its catalogue description as a hover title. Shared by the
 * workspace Roles page and the helpdesk Roles page.
 */

import { Popover, PopoverContent, PopoverTrigger } from "@cometchat-ai/ui-dashboard";
import { describePermission, permissionActionLabel } from "@cometchat-ai/auth/shared";
import { Fragment } from "react";

function subjectLabel(subject: string): string {
  if (subject === "kb") return "Knowledge";
  return subject.charAt(0).toUpperCase() + subject.slice(1).replace(/-/g, " ");
}

function actionLabel(slug: string): string {
  const override = permissionActionLabel(slug);
  if (override) return override;
  const action = slug.slice(slug.indexOf(":") + 1);
  return action.charAt(0).toUpperCase() + action.slice(1).replace(/-/g, " ");
}

export function PermissionCountPill({
  label,
  permissions,
  className,
}: {
  /** Pill text, e.g. the i18n'd "22 permissions". */
  label: string;
  permissions: ReadonlyArray<string>;
  className?: string;
}) {
  const groups = new Map<string, string[]>();
  for (const slug of permissions) {
    const subject = slug.slice(0, slug.indexOf(":"));
    const arr = groups.get(subject);
    if (arr) arr.push(slug);
    else groups.set(subject, [slug]);
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={
            className ??
            "inline-flex shrink-0 cursor-pointer items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          }
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="max-h-80 space-y-3 overflow-y-auto p-4">
          {[...groups.entries()].map(([subject, slugs]) => (
            <div key={subject} className="space-y-0.5">
              <div className="text-xs font-medium text-muted-foreground">
                {subjectLabel(subject)}
              </div>
              <div className="text-[13px] leading-relaxed text-foreground">
                {slugs.map((slug, i) => (
                  <Fragment key={slug}>
                    {i > 0 ? <span className="text-muted-foreground">, </span> : null}
                    <span title={describePermission(slug) ?? slug} className="cursor-default">
                      {actionLabel(slug)}
                    </span>
                  </Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
